import { z } from 'zod';
import { stampedBody } from '../common/row-version.js';
import { API_CLIENT_SELF_GRANTABLE_KEYS, PROJECT_GRANTABLE_KEYS } from '../permissions/catalogue.js';
import { DeviceAccessServiceKindSchema } from '../assets/access.js';
// api_clients admin writes (ADR-004 — the sysadmin platform surface).
//
// Two permission lists, validated against the catalogue-derived sets so an
// unknown / wrong-tier key fails as a 400 at the schema:
//   permissions           — SELF (pure M2M): machine-tier + human capability keys
//                           (everything but platform-tier).
//   on_behalf_permissions — brokered (∩ the human): the project-grantable set
//                           only — machine-tier keys are meaningless here (no
//                           human holds them, the intersection could never pass).
export const ApiClientSelfPermissionSchema = z.enum(API_CLIENT_SELF_GRANTABLE_KEYS);
export const ApiClientOnBehalfPermissionSchema = z.enum(PROJECT_GRANTABLE_KEYS);
export const CreateApiClientRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(64),
    description: z.string().trim().min(1).max(500).nullish(),
    // Reach: exactly one project id, or null = fleet (every project — first-party
    // services only; the UI labels it so).
    project_id: z.string().max(16).nullable(),
    permissions: z.array(ApiClientSelfPermissionSchema).default([]),
    on_behalf_permissions: z.array(ApiClientOnBehalfPermissionSchema).default([]),
    // device-targets narrowing (DATA-CONTRACTS §device targets): which
    // credential-bearing service kinds this credential receives — an entry
    // travels whole or not at all. Empty = identity-only (reach facts + the
    // fronted composition, zero secrets); inert without device_targets:read.
    device_target_kinds: z.array(DeviceAccessServiceKindSchema).default([]),
})
    .strict();
export const UpdateApiClientRequestSchema = z
    .object({
    ...stampedBody,
    name: z.string().trim().min(1).max(64).optional(),
    description: z.string().trim().max(500).nullable().optional(), // null clears
    project_id: z.string().max(16).nullable().optional(),
    permissions: z.array(ApiClientSelfPermissionSchema).optional(),
    on_behalf_permissions: z.array(ApiClientOnBehalfPermissionSchema).optional(),
    device_target_kinds: z.array(DeviceAccessServiceKindSchema).optional(),
})
    .strict();
// The rotation overlap the operator chooses (Stripe's menu). 'none' = the old key
// dies immediately; on a REVOKED client rotate is re-key and overlap is ignored
// (a dead credential never returns).
export const ROTATION_OVERLAPS = ['none', '1h', '24h', '7d'];
export const RotateApiClientRequestSchema = z
    .object({ overlap: z.enum(ROTATION_OVERLAPS) })
    .strict();
